import { Link } from 'react-router-dom';
import { Download, ArrowLeft } from 'lucide-react';
import PageShell from '@/components/PageShell';

/* ================================================================
   /brand/banner — LinkedIn banner options for TMG.
   1128x191 (rendered 2x @ 2256x382 for retina crispness).
   Five options, each with technology / data / mobility language.
================================================================ */

const BANNERS = [
  {
    id: 1, key: 'constellation',
    title: 'Constellation network',
    tagline: 'Nodes, hair-lines, one hub',
    rationale:
      'A subtle network of amber nodes connected by hair-lines on the right — reads as the data ecosystem you operate. Feels alive without being busy. Our recommendation for a technology-forward first impression.',
    file: '/brand/tmg-linkedin-banner-01-constellation.png',
  },
  {
    id: 2, key: 'grid-pulse',
    title: 'Data grid · KPIs',
    tagline: 'Regular grid with three FY26 stats',
    rationale:
      'A quiet dot-grid across the whole banner with amber highlights, and three FY26 numbers pulled from the site (397M+, 29.7M, 80+). Editorial and data-forward — good if you want the banner to double as a fact sheet.',
    file: '/brand/tmg-linkedin-banner-02-grid-pulse.png',
  },
  {
    id: 3, key: 'perspective',
    title: 'Motion perspective',
    tagline: 'Converging lines into a distant star',
    rationale:
      'Perspective lines from every edge converge to a small amber star on the right — highway and vanishing-point language, direct reference to mobility. Cinematic. Best if you want the banner to feel like it is moving.',
    file: '/brand/tmg-linkedin-banner-03-perspective.png',
  },
  {
    id: 4, key: 'ghost-kpis',
    title: 'Ghost KPIs',
    tagline: 'Big translucent numbers behind the brand',
    rationale:
      'Giant translucent stats (397M+, 28K+) sit as ghosts on the right, thin grid rules run vertically. Feels like a research house cover — sophisticated, editorial, data-heavy without shouting.',
    file: '/brand/tmg-linkedin-banner-04-ghost-kpis.png',
  },
  {
    id: 5, key: 'signal',
    title: 'Signal wave',
    tagline: 'Amber sine wave · data-in-motion',
    rationale:
      'A single amber sine wave running from the wordmark across the right with data-point dots along it. The simplest of the five; reads as a live signal / heartbeat / data stream. Perfect if you want something quiet and modern.',
    file: '/brand/tmg-linkedin-banner-05-signal.png',
  },
];

const Card = ({ opt }) => (
  <article
    data-testid={`banner-${opt.key}`}
    className="border border-white/10 bg-ink-800 overflow-hidden"
  >
    {/* Big preview at true LinkedIn aspect ratio */}
    <div className="relative bg-black">
      <img
        src={`${opt.file}?v=9`}
        alt={`TMG LinkedIn banner — ${opt.title}`}
        className="w-full h-auto block"
      />
      <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] text-amber">
        / 0{opt.id} · {opt.key}
      </div>
      <div className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        1128 × 191 · 2x
      </div>
    </div>

    {/* Meta */}
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-8 border-t border-white/10">
      <div className="lg:col-span-7">
        <h3 className="font-display text-2xl md:text-3xl text-white">
          {opt.title}
        </h3>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber mt-1">
          {opt.tagline}
        </div>
        <p className="mt-4 text-zinc-400 text-sm leading-relaxed max-w-2xl">
          {opt.rationale}
        </p>
      </div>
      <div className="lg:col-span-5 flex items-start lg:items-center gap-3 flex-wrap">
        <a
          href={opt.file}
          download=""
          data-testid={`download-banner-${opt.key}`}
          className="inline-flex items-center gap-2 border border-amber bg-amber px-5 py-3 text-[11px] font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Download PNG
        </a>
        <a
          href={opt.file}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`view-banner-${opt.key}`}
          className="inline-flex items-center gap-2 border border-white/25 px-5 py-3 text-[11px] font-mono uppercase tracking-[0.18em] text-white hover:border-amber hover:text-amber transition-colors"
        >
          Open full-size
        </a>
      </div>
    </div>
  </article>
);

const BrandBanner = () => {
  return (
    <PageShell>
      {/* Top nav crumb */}
      <div className="px-6 md:px-12 lg:px-16 pt-8">
        <Link
          to="/brand"
          data-testid="banner-back-to-brand"
          className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-amber inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> brand
        </Link>
      </div>

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-16 pt-8 pb-12 md:pb-16 border-b border-white/10">
        <div className="overline text-amber mb-4">
          <span className="inline-flex items-center gap-2">
            <span className="h-px w-6 bg-amber inline-block" />
            Brand · LinkedIn banner
          </span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-balance max-w-4xl leading-[0.98]">
          Five banners.
          <br />
          <span className="text-amber">Technology, data, mobility.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-base md:text-lg text-zinc-300 leading-relaxed">
          Five cover-image options for the LinkedIn company page — each
          rendered at LinkedIn&apos;s spec (1128 × 191) but exported at 2×
          for crisp retina display. Every banner keeps the amber star,{' '}
          <span className="text-amber">The Mobility Group</span> wordmark and
          the tagline anchored on the left; the right side is where each
          option tells a different story about the group.
        </p>
        <p className="mt-3 max-w-3xl text-sm text-zinc-500 leading-relaxed">
          Pick one, hit <span className="text-amber">Download PNG</span>, then
          go to LinkedIn → Company page → Edit → Cover image → upload.
        </p>
      </section>

      {/* Cards */}
      <section className="px-6 md:px-12 lg:px-16 py-12 md:py-16 space-y-10">
        {BANNERS.map((opt) => (
          <Card key={opt.id} opt={opt} />
        ))}
      </section>

      {/* Recommendation */}
      <section className="px-6 md:px-12 lg:px-16 py-16 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="overline text-amber mb-3">Our recommendation</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              <span className="text-amber">Option 01</span> or{' '}
              <span className="text-amber">Option 03</span>.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
              Option 01 (Constellation) reads as the ecosystem you actually
              operate — networks, data, connections — and matches the visual
              language on the site&apos;s Use Cases page. Option 03 (Motion
              perspective) is more cinematic and directly signals{' '}
              <span className="text-amber">mobility</span> — appropriate if
              you want the banner to feel forward-moving.
            </p>
            <p className="mt-4 text-zinc-500 text-xs leading-relaxed">
              Options 02 (KPI grid) and 04 (Ghost KPIs) are quieter,
              data-forward. Option 05 (Signal wave) is the simplest and
              cleanest. All five are safe for mobile crop (nothing critical
              near the edges).
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default BrandBanner;
